import React from 'react';
import { Avatar, Tag } from 'antd';
import i18next from 'i18next';
import { CloseOutlined, UserOutlined } from '@ant-design/icons';

import { ENQUIRY_TYPES_CONST } from 'configs/localData';
import { useSelector } from 'react-redux';
import { getCurrentEnquiry } from 'redux/enquiries/selectors';
import { KindiCareIcon, MailIcon, PhoneIcon } from 'components/common/SVGIcon';
import { useHistory } from 'react-router';
import EmptyData from 'components/common/EmptyData';
import { useEnquiryContext } from 'containers/Enquiries/List/context';
import { getCentresObjKeyById } from 'redux/auth/selectors';
import CheckPermission from 'components/common/CheckPermissions';
import HeaderUserInfoEnquiryFallback from 'components/RestActions/HeaderUserInfoEnquiry';
import CustomText from 'components/RestActions/CustomText';
import TagCustom from 'components/common/TagCustom';
import theme from 'configs/theme';
import ChildrenItem from './ChildrenItem';
import ContactEnquiryStyles from './styles';

const ContactEnquiry = () => {
  const history = useHistory();
  const { user, enquiryType, parentRequestDetails, centreId, isBlur } =
    useSelector(getCurrentEnquiry) || {};

  const { setCollapsed } = useEnquiryContext();

  const packageName: string =
    useSelector(getCentresObjKeyById)?.[centreId]?.centreSubscription
      ?.CRMPackage?.name;

  return (
    <ContactEnquiryStyles>
      {user?.id ? (
        <>
          <div className="contact-enquiry__header flex-center-between">
            <div className="user-info">
              <CheckPermission
                packageName={packageName}
                permissionKey="contacts"
                action="isEdit"
                isDisabled
                fallback={
                  <HeaderUserInfoEnquiryFallback
                    centreId={centreId}
                    isBlur={isBlur}
                    user={user}
                    isContactSession
                  />
                }
              >
                <div
                  className="left-section flex items-center cursor-pointer"
                  role="presentation"
                  onClick={() =>
                    user?.contactId &&
                    history.push(`/contacts/list/${user.contactId}/show`)
                  }
                >
                  <Avatar
                    className="mr-10"
                    size={40}
                    src={user?.avatar}
                    icon={<UserOutlined />}
                  />
                  <div className="info-section">
                    <div className="section-title">
                      {user?.firstName || ''}
                      <span className={isBlur ? 'blur' : ''}>{` ${
                        user?.lastName || ''
                      }`}</span>
                    </div>
                  </div>
                </div>
              </CheckPermission>
            </div>

            <CloseOutlined
              className="close-icon pl-10 cursor-pointer"
              onClick={() => setCollapsed(true)}
            />
          </div>

          <div className="contact-enquiry__body">
            <div className="contact-enquiry__section">
              <div className="contact-enquiry__section-title">
                {i18next.t('title.about')}
              </div>

              <div className="contact-enquiry__section-info">
                <div className="item-info">
                  <CustomText
                    isBlur={isBlur}
                    icon={<PhoneIcon className="text-primary mr-8" />}
                    textValue={
                      user?.phoneNumber || i18next.t('error.waitingUpdate')
                    }
                  />
                </div>

                <div className="item-info mt-16">
                  <CustomText
                    isBlur={isBlur}
                    icon={<MailIcon className="text-primary mr-8" />}
                    textValue={user?.email || i18next.t('error.waitingUpdate')}
                    centreId={centreId}
                  />
                </div>
              </div>
            </div>
            {user?.preferredContactMethod && (
              <div className="contact-enquiry__section">
                <div className="contact-enquiry__section-title">
                  {i18next.t('enquiries.preferredContactMethod')}
                </div>
                <TagCustom
                  text={user?.preferredContactMethod}
                  IconCPN={KindiCareIcon}
                  color={theme.palette.primary}
                  backgroundColor={theme.color.lightPink}
                  className="fw-600"
                />
              </div>
            )}
            <div className="contact-enquiry__section">
              <div className="contact-enquiry__section-title">
                {i18next.t('enquiries.enquiryReason')}
              </div>

              <div className="contact-enquiry__section-info">
                <div className="enquiry-type">
                  {ENQUIRY_TYPES_CONST[enquiryType?.name] && (
                    <Tag color={ENQUIRY_TYPES_CONST[enquiryType.name].color}>
                      {i18next.t(ENQUIRY_TYPES_CONST[enquiryType.name].text)}
                    </Tag>
                  )}
                </div>
              </div>
            </div>

            {parentRequestDetails?.length ? (
              <div className="contact-enquiry__section">
                <div className="contact-enquiry__section-title">
                  {i18next.t('applications.childInformation')}
                </div>

                <div className="contact-enquiry__section-info">
                  {parentRequestDetails.map((item, idx) => (
                    <ChildrenItem key={String(idx)} item={item} />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <EmptyData className="p-12" />
      )}
    </ContactEnquiryStyles>
  );
};

export default ContactEnquiry;
